import { DEFAULT_LOCALE, type Locale } from '@syncanix/shared';

import { type CatalogGroup } from './catalog-groups';
import { loadCatalog } from './load-catalog';
import { createTranslator, type MarketingCatalog, type Translator } from './translator';

/**
 * The one mutable catalog seam between the group-aware loader and React
 * (ADR-0195). `I18nProvider` subscribes; `entry-client`, `LocaleSync` and the
 * lazy-route factories in `routes.tsx` drive it:
 *
 *   - `entry-client` awaits `ensureCatalog(urlLocale, groupsForPath(...))`
 *     BEFORE hydrating and seeds the provider with the resolved translator.
 *   - Route factories call `ensureGroups([...])` so an SPA navigation's route
 *     chunk does not resolve until its strings are loaded — no key-echo flash.
 *   - `LocaleSync` calls `ensureCatalog` on URL changes (locale switches).
 *
 * Required groups accumulate for the session (navigating home from /docs
 * keeps `docs` resolved — pieces are cached, so this costs nothing) and the
 * whole accumulated set is re-resolved when the locale changes.
 */

export type CatalogLoadFn = (
  locale: Locale,
  groups: readonly CatalogGroup[],
) => Promise<MarketingCatalog>;

export interface CatalogStore {
  /** Load (or extend) the catalog for `locale`; resolves the new translator. */
  ensureCatalog(locale: Locale, groups: readonly CatalogGroup[]): Promise<Translator>;
  /** Extend the ACTIVE locale's catalog with `groups`. */
  ensureGroups(groups: readonly CatalogGroup[]): Promise<Translator>;
  /** Subscribe to translator changes; returns the unsubscribe. */
  subscribe(listener: () => void): () => void;
  /** The current translator (key-echo until the first resolution). */
  getTranslator(): Translator;
  /** The locale of the latest `ensureCatalog` call (default: EN). */
  getActiveLocale(): Locale;
}

/** Build a store over an injectable loader (the seam the tests use). */
export function createCatalogStore(load: CatalogLoadFn): CatalogStore {
  let activeLocale: Locale = DEFAULT_LOCALE;
  const requiredGroups = new Set<CatalogGroup>(['core']);
  let translator: Translator = createTranslator({});
  /** `<locale>|<sorted groups>` the current translator reflects (`''` = none). */
  let resolvedKey = '';
  let generation = 0;
  /** The newest ensure's resolution — superseded callers defer to it. */
  let newest: Promise<Translator> | null = null;
  /** In-flight resolutions by target, so identical concurrent ensures JOIN
   *  (the /docs boot race: entry-client and the route factory request the
   *  same target; treating the second as superseding made the first resolve
   *  to the initial key-echo translator and seed hydration with raw keys). */
  const inflight = new Map<string, Promise<Translator>>();
  const listeners = new Set<() => void>();

  function wantedKey(locale: Locale): string {
    return `${locale}|${[...requiredGroups].sort().join(',')}`;
  }

  function ensureCatalog(locale: Locale, groups: readonly CatalogGroup[]): Promise<Translator> {
    activeLocale = locale;
    for (const group of groups) requiredGroups.add(group);
    const want = wantedKey(locale);
    if (want === resolvedKey) return Promise.resolve(translator);
    const joined = inflight.get(want);
    if (joined !== undefined) return joined;

    const gen = ++generation;
    const resolution: Promise<Translator> = load(locale, [...requiredGroups]).then((catalog) => {
      inflight.delete(want);
      if (gen !== generation) {
        // Superseded while in flight — the newer ensure owns the state; hand
        // the caller ITS translator (never the possibly-key-echo current one).
        return newest !== null && newest !== resolution ? newest : translator;
      }
      translator = createTranslator(catalog);
      resolvedKey = want;
      for (const listener of listeners) listener();
      return translator;
    });
    newest = resolution;
    inflight.set(want, resolution);
    return resolution;
  }

  return {
    ensureCatalog,
    ensureGroups: (groups) => ensureCatalog(activeLocale, groups),
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getTranslator: () => translator,
    getActiveLocale: () => activeLocale,
  };
}

/** The app-wide store, bound to the virtual-chunk loader. */
export const catalogStore: CatalogStore = createCatalogStore(loadCatalog);
